"use client"

import { useState, useEffect } from "react"
import { FaEdit, FaTrash } from "react-icons/fa"
import { IoMdAdd, IoMdSearch } from "react-icons/io"
import ProductModal from "../components/ProductModal.js"
import { fetchProducts, addProduct, updateProduct, deleteProduct } from "../comman/Product.js"
import Sidebar from "../components/Sidebar.js"

const ProductTable = () => {
  const [data, setData] = useState([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [productToEdit, setProductToEdit] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchQuery, setSearchQuery] = useState("")

  const getProducts = async () => {
    try {
      const products = await fetchProducts()
      setData(products)
    } catch (err) {
      setError("Error fetching products")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    getProducts()
  }, [])

  const handleAddProduct = async (newProductData) => {
    try {
      await addProduct(newProductData)
      await getProducts()
      setIsModalOpen(false)
    } catch (err) {
      setError("Error adding product")
    }
  }

  const handleUpdateProduct = async (updatedProductData) => {
    try {
      await updateProduct(updatedProductData)
      await getProducts()
      setIsModalOpen(false)
    } catch (err) {
      setError("Error updating product")
    }
  }

  const handleDelete = async (productId) => {
    if (window.confirm("Are you sure you want to delete this product?")) {
      try {
        await deleteProduct(productId)
        await getProducts()
      } catch (err) {
        setError("Error deleting product")
      }
    }
  }

  const handleEdit = (product) => {
    setProductToEdit(product)
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setProductToEdit(null) // Reset productToEdit
  }

  const filteredProducts = data.filter(
    (product) =>
      product.productName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.coin.toString().toLowerCase().includes(searchQuery.toLowerCase()),
  )

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-[#12143F]"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-red-500 text-xl">{error}</div>
      </div>
    )
  }

  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-gray-100">
      <Sidebar />
      <div className="flex-1 p-4 md:p-6 md:ml-64">
        <h1 className="text-2xl md:text-3xl font-bold text-[#12143F] mb-6">Product Management</h1>
        <div className="bg-white rounded-lg shadow-md p-4 md:p-6">
          <div className="flex flex-col md:flex-row justify-between items-center mb-6 space-y-4 md:space-y-0 md:space-x-4">
            <div className="relative w-full md:w-64">
              <input
                type="text"
                className="w-full pl-10 pr-4 py-2 text-sm text-gray-900 border border-gray-300 rounded-lg focus:ring-[#12143F] focus:border-[#12143F]"
                placeholder="Search products"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <IoMdSearch className="w-5 h-5 text-gray-500" />
              </div>
            </div>
            <button
              onClick={() => setIsModalOpen(true)}
              className="flex items-center px-4 py-2 bg-[#12143F] text-white rounded-md hover:bg-opacity-90 transition duration-300"
            >
              <IoMdAdd size={18} className="mr-2" />
              Add Product
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left text-gray-500">
              <thead className="text-xs text-white uppercase bg-[#12143F]">
                <tr>
                  <th scope="col" className="px-6 py-3">
                    Sr. No
                  </th>
                  <th scope="col" className="px-6 py-3">
                    Product Name
                  </th>
                  <th scope="col" className="px-6 py-3">
                    Product Image
                  </th>
                  <th scope="col" className="px-6 py-3">
                    Coin
                  </th>
                  <th scope="col" className="px-6 py-3">
                    Description
                  </th>
                  <th scope="col" className="px-6 py-3">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredProducts.map((row, index) => (
                  <tr key={row._id} className="border-b hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium text-gray-900">{index + 1}</td>
                    <td className="px-6 py-4">{row.productName}</td>
                    <td className="px-6 py-4">
                      {row.productImage ? (
                        <img
                          src={row.productImage || "/placeholder.svg"}
                          alt={row.productName}
                          className="w-20 h-12 object-cover border border-gray-200 rounded"
                        />
                      ) : (
                        <span className="text-gray-400">No Image</span>
                      )}
                    </td>
                    <td className="px-6 py-4">{row.coin}</td>
                    <td className="px-6 py-4">{row.description}</td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => handleEdit(row)}
                        className="text-[#12143F] hover:text-opacity-80 transition-colors duration-200 mr-3"
                      >
                        <FaEdit size={18} />
                      </button>
                      <button
                        onClick={() => handleDelete(row._id)}
                        className="text-red-500 hover:text-red-700 transition-colors duration-200"
                      >
                        <FaTrash size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {isModalOpen && (
        <ProductModal
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          productToEdit={productToEdit}
          onAddProduct={handleAddProduct}
          onUpdateProduct={handleUpdateProduct}
          onClose={handleCloseModal}
        />
      )}
    </div>
  )
}

export default ProductTable